import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Building2, Users, FileText, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";

const steps = [
  {
    number: "01",
    icon: Building2,
    title: "Créez votre chantier",
    description: "Nom, client, devis, dates. 2 minutes chrono.",
  },
  {
    number: "02",
    icon: Users,
    title: "Affectez votre équipe",
    description: "Sélectionnez les membres. Les coûts se calculent automatiquement.",
  },
  {
    number: "03",
    icon: FileText,
    title: "Importez vos factures",
    description: "Factures et devis. Le Scan IA extrait tout. Fini les ressaisies.",
  },
  {
    number: "04",
    icon: BarChart3,
    title: "Pilotez en temps réel",
    description: "Marge, alertes, Jour Critique. Tout en un coup d'œil.",
  },
];

// Seuils : chaque step s'allume à ces % de progression
const THRESHOLDS = [0.0, 0.25, 0.50, 0.75];

// Durées de l'animation (ms)
const INITIAL_DELAY = 200; // délai avant que le step 1 s'allume
const STEP_INTERVAL = 700; // 700ms entre chaque step
const LINE_DURATION = 700; // la mèche met 700ms pour parcourir un segment

const LandingHowItWorks = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasTriggered = useRef(false);

  const [activeStep, setActiveStep] = useState(-1);
  const [lineTarget, setLineTarget] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered.current) {
          hasTriggered.current = true;

          // Séquence : step 0 s'allume → mèche part vers step 1 → step 1 s'allume à l'arrivée → etc.
          // Chaque cycle = STEP_INTERVAL entre les départs de mèche
          // La box s'allume quand la mèche arrive (= début du trajet + LINE_DURATION)

          // Step 0 s'allume tout de suite (point de départ)
          setTimeout(() => setActiveStep(0), INITIAL_DELAY);

          for (let i = 1; i <= 3; i++) {
            const departMeche = INITIAL_DELAY + i * STEP_INTERVAL;

            // La mèche part vers le step i
            setTimeout(() => {
              setLineTarget((i / 3) * 100); // 33%, 66%, 100%
            }, departMeche);

            // Le step i s'allume quand la mèche arrive
            setTimeout(() => {
              setActiveStep(i);
            }, departMeche + LINE_DURATION);
          }
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const activeCount = activeStep + 1; // 0 à 4
  const lineProgress = lineTarget;

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="relative py-24 lg:py-32 bg-[#08080C]"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#8B5CF6]/5 rounded-full blur-[150px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        {/* Section header */}
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-wider text-white/40 font-medium mb-4 animate-fade-up">
            Comment ça marche
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight animate-fade-up [animation-delay:100ms]">
            Opérationnel en
            <span className="block bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] bg-clip-text text-transparent">
              10 minutes.
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/50 animate-fade-up [animation-delay:200ms]">
            Aucune formation nécessaire. 4 étapes simples.
          </p>
        </div>

        {/* ═══════ Horizontal Timeline — Desktop ═══════ */}
        <div className="hidden lg:block max-w-6xl mx-auto mb-16">
          <div className="relative">
            {/* Connecting line (background — éteint) */}
            <div className="absolute top-[27px] left-[12.5%] right-[12.5%] h-[2px] bg-white/[0.06]">
              {/* Ligne allumée (overlay) */}
              <div
                className="absolute top-0 left-0 h-full rounded-full"
                style={{
                  width: `${lineProgress}%`,
                  background: "linear-gradient(90deg, #F59E0B, #D4AF37)",
                  boxShadow: lineProgress > 2 ? "0 0 8px rgba(245,158,11,0.4)" : "none",
                  transition: `width ${LINE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              />
              {/* Faisceau (tête lumineuse) */}
              {lineProgress > 1 && lineProgress < 99 && (
                <div
                  className="absolute top-[-4px] h-[10px] w-[30px] rounded-full pointer-events-none"
                  style={{
                    left: `calc(${lineProgress}% - 15px)`,
                    background: "radial-gradient(ellipse, rgba(255,255,255,0.7), rgba(245,158,11,0.4), transparent)",
                    filter: "blur(2px)",
                    transition: `left ${LINE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                  }}
                />
              )}
            </div>

            {/* Steps */}
            <div className="grid grid-cols-4 gap-6">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isLit = index < activeCount;
                const isJustLit = index === activeCount - 1;

                return (
                  <div key={index} className="relative">
                    {/* Step number box */}
                    <div className="relative mb-10 flex justify-center">
                      <div
                        className="relative z-10 h-14 w-14 rounded-[10px] flex items-center justify-center font-bold text-lg"
                        style={{
                          background: "#131318",
                          border: isLit ? "1px solid #F59E0B" : "1px solid rgba(255,255,255,0.06)",
                          color: isLit ? "#FFFFFF" : "rgba(255,255,255,0.3)",
                          boxShadow: isLit
                            ? "0 0 20px rgba(245,158,11,0.25), 0 0 40px rgba(245,158,11,0.08)"
                            : "none",
                          transform: isJustLit ? "scale(1.05)" : "scale(1)",
                          transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)",
                          willChange: "transform, box-shadow, border-color, color",
                        }}
                      >
                        {step.number}
                      </div>
                    </div>

                    {/* Card */}
                    <div
                      className="group rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.08] to-transparent hover:from-white/[0.15] transition-all duration-300"
                      style={{
                        opacity: isLit ? 1 : 0.3,
                        transform: isLit ? "translateY(0)" : "translateY(10px)",
                        transition: "opacity 0.5s ease, transform 0.5s ease",
                        willChange: "opacity, transform",
                      }}
                    >
                      <div className="rounded-2xl bg-[#0F0F14]/80 backdrop-blur-sm p-6 text-center hover:bg-[#0F0F14] transition-colors duration-300 h-full">
                        <div className="h-10 w-10 mx-auto rounded-[10px] bg-[#131318] border border-white/[0.06] flex items-center justify-center mb-4">
                          <Icon className="h-[18px] w-[18px] text-white/50" strokeWidth={2.5} />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          {step.title}
                        </h3>
                        <p className="text-sm text-white/50 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ═══════ Vertical Timeline — Mobile/Tablet ═══════ */}
        <div className="lg:hidden max-w-md mx-auto">
          <div className="relative">
            {/* Vertical line (éteint) */}
            <div className="absolute left-7 top-0 bottom-0 w-[2px] bg-white/[0.06]">
              {/* Ligne allumée */}
              <div
                className="absolute top-0 left-0 w-full rounded-full"
                style={{
                  height: `${lineProgress}%`,
                  background: "linear-gradient(180deg, #F59E0B, #D4AF37)",
                  boxShadow: lineProgress > 2 ? "0 0 6px rgba(245,158,11,0.3)" : "none",
                  transition: `height ${LINE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              />
            </div>

            {/* Steps */}
            <div className="space-y-6">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isLit = index < activeCount;

                return (
                  <div
                    key={index}
                    className="relative pl-20"
                    style={{
                      opacity: isLit ? 1 : 0.3,
                      transform: isLit ? "translateY(0)" : "translateY(8px)",
                      transition: "opacity 0.5s ease, transform 0.5s ease",
                    }}
                  >
                    {/* Step number */}
                    <div
                      className="absolute left-0 h-14 w-14 rounded-[10px] flex items-center justify-center font-bold text-lg z-10"
                      style={{
                        background: "#131318",
                        border: isLit ? "1px solid #F59E0B" : "1px solid rgba(255,255,255,0.06)",
                        color: isLit ? "#FFFFFF" : "rgba(255,255,255,0.3)",
                        boxShadow: isLit
                          ? "0 0 16px rgba(245,158,11,0.2)"
                          : "none",
                        transition: "all 0.4s ease",
                      }}
                    >
                      {step.number}
                    </div>

                    {/* Card */}
                    <div className="group rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.08] to-transparent">
                      <div className="rounded-2xl bg-[#0F0F14]/80 backdrop-blur-sm p-5">
                        <div className="flex items-start gap-4">
                          <div className="h-10 w-10 rounded-[10px] bg-[#131318] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                            <Icon className="h-[18px] w-[18px] text-white/50" strokeWidth={2.5} />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-white mb-1">
                              {step.title}
                            </h3>
                            <p className="text-sm text-white/50 leading-relaxed">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center animate-fade-up [animation-delay:800ms]">
          <Button
            size="lg"
            className="h-12 px-6 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base gap-2.5 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_8px_30px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
            asChild
          >
            <Link to="/auth?mode=signup">
              Essayer gratuitement 14 jours
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default LandingHowItWorks;
